import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

dayjs.extend(duration);
dayjs.extend(utc);

export const computeDuration = (durationTime: number): string => {
  const duration = Number(durationTime).toFixed(2);
  const seconds = Number(duration.substring(0, duration.indexOf('.') + 1));
  const time = dayjs.duration(seconds, 'seconds');

  return time.hours() > 0 ? time.format('HH:mm:ss') : time.format('mm:ss');
};

export const isWithinTwoHourEventWindow = (
  scheduleDateUtc: string
): boolean => {
  const scheduledTime = dayjs(scheduleDateUtc);
  const currentTime = dayjs();

  return (
    scheduledTime.isAfter(currentTime.add(-1, 'hour')) &&
    scheduledTime.isBefore(currentTime.add(1, 'hour'))
  );
};

// added for formatting interview date and time labels so we don't display time with "UTC+X" or "GMT+X" to the users
const IANA_TO_ABBREV: Record<string, string> = {
  'Europe/London': 'GMT',
  'Europe/Berlin': 'CET',
  'Europe/Paris': 'CET',
  'Europe/Belgrade': 'CET',
  'Europe/Zagreb': 'CET',
  'Europe/Budapest': 'CET',
  'Europe/Prague': 'CET',
  'Europe/Warsaw': 'CET',
  'Europe/Amsterdam': 'CET',
  'Europe/Brussels': 'CET',
  'Europe/Vienna': 'CET',
  'Europe/Rome': 'CET',
  'Europe/Madrid': 'CET',
  'Europe/Stockholm': 'CET',
  'Europe/Athens': 'EET',
  'Europe/Helsinki': 'EET',
  'Europe/Bucharest': 'EET',
  'Europe/Sofia': 'EET',
  'Europe/Moscow': 'MSK',
  'Europe/Kiev': 'EET',
  // US & Canada – Eastern
  'America/New_York': 'ET',
  'America/Detroit': 'ET',
  'America/Indianapolis': 'ET',
  'America/Montreal': 'ET',
  'America/Toronto': 'ET',
  // US & Canada – Central
  'America/Chicago': 'CT',
  'America/Winnipeg': 'CT',
  'America/Regina': 'CST',
  // US & Canada – Mountain
  'America/Denver': 'MT',
  'America/Edmonton': 'MT',
  'America/Calgary': 'MT',
  'America/Phoenix': 'MST',
  // US & Canada – Pacific
  'America/Los_Angeles': 'PT',
  'America/Vancouver': 'PT',
  'America/Seattle': 'PT',
  'America/Portland': 'PT',
  // US – Alaska & Hawaii
  'America/Anchorage': 'AKT',
  'America/Juneau': 'AKT',
  'Pacific/Honolulu': 'HST',
  // Canada – Atlantic
  'America/Halifax': 'AT',
  'America/St_Johns': 'NT',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Asia/Dubai': 'GST',
  'Australia/Sydney': 'AEST',
  'Australia/Melbourne': 'AEST',
};

export const getTimezoneAbbreviation = () => {
  const date = new Date();
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZoneName: 'short',
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    const value = tzPart?.value ?? '';
    if (value && !/^GMT[+-]\d+$/.test(value)) {
      return value;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const abbrev = timeZone ? IANA_TO_ABBREV[timeZone] : undefined;
    if (abbrev) {
      return abbrev;
    }
    const offset = -date.getTimezoneOffset() / 60;
    return `UTC${offset >= 0 ? '+' : ''}${offset}`;
  } catch {
    const offset = -date.getTimezoneOffset() / 60;
    return `UTC${offset >= 0 ? '+' : ''}${offset}`;
  }
};

export const formatInterviewDateLabel = (scheduleDateUtc: string): string => {
  const eventDate = dayjs.utc(scheduleDateUtc).local();
  const today = dayjs();
  if (eventDate.isSame(today, 'day')) {
    return 'Today';
  }
  if (eventDate.isSame(today.add(1, 'day'), 'day')) {
    return 'Tomorrow';
  }
  return eventDate.format('ddd, MMMM D');
};

export const formatInterviewTimeLabel = (scheduleDateUtc: string): string => {
  const tz = getTimezoneAbbreviation();
  return `${dayjs.utc(scheduleDateUtc).local().format('h:mm A')} ${tz}`;
};

export type CompactTimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const padTwo = (n: number): string => String(n).padStart(2, '0');

export const getCompactTimeLeft = (
  deadlineUtc: string,
  now = Date.now()
): CompactTimeLeft | null => {
  const target = new Date(deadlineUtc).getTime();
  if (Number.isNaN(target)) {
    return null;
  }

  const difference = target - now;
  if (difference <= 0) {
    return null;
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
  };
};

export const formatCompactCountdown = (timeLeft: CompactTimeLeft): string => {
  if (timeLeft.days > 0) {
    return `${padTwo(timeLeft.days)}d : ${padTwo(timeLeft.hours)}h : ${padTwo(
      timeLeft.minutes
    )}m`;
  }
  return `${padTwo(timeLeft.hours)}h : ${padTwo(timeLeft.minutes)}m : ${padTwo(
    timeLeft.seconds
  )}s`;
};
